import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageHero from '@/components/feature/PageHero';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { cases, caseTypes, caseStatuses } from '@/mocks/cases';

const statusStyle: Record<string, string> = {
  已验证: 'bg-accent-100 text-accent-900',
  推进中: 'bg-secondary-100 text-secondary-900',
  待授权: 'bg-background-200 text-foreground-600',
};

export default function Cases() {
  const [type, setType] = useState('全部类型');
  const [status, setStatus] = useState('全部状态');

  const filtered = useMemo(() => {
    return cases.filter((c) => {
      const matchType = type === '全部类型' || c.type.includes(type);
      const matchStatus = status === '全部状态' || c.status === status;
      return matchType && matchStatus;
    });
  }, [type, status]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <SEO
        title="客户案例 | 项目索引与客户覆盖范围"
        description="华辉数智及九畴云市的项目列表与客户覆盖范围，展示已确认、已验证、可核验的项目与成果。"
        keywords="客户案例,项目索引,帝星数商综合体,大庄科样板间,九畴云市"
        path="/cases"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: '客户案例',
          description: '华辉数智及九畴云市的项目列表与客户覆盖范围',
        }}
      />
      <PageHero
        eyebrow="客户案例"
        title="项目索引与客户覆盖"
        titleHighlight="已确认 · 已验证 · 可核验"
        subtitle="展示华辉数智及九畴云市的项目列表与客户覆盖范围。"
        desc="所有案例数据、客户名称、合作关系与效果指标均使用已确认信息，不虚构结果。"
        imageUrl="https://readdy.ai/api/search-image?query=Abstract%20case%20study%20portfolio%20with%20interconnected%20project%20cards%2C%20network%20nodes%20connecting%20city%20and%20rural%20scenarios%2C%20digital%20commerce%20achievements%20and%20verified%20milestones%2C%20deep%20graphite%20background%20with%20green%20and%20gold%20accents%2C%20clean%20minimal%20composition%2C%20cinematic%20lighting&width=1600&height=900&seq=cases-hero-01&orientation=landscape"
        imageAlt="客户案例项目索引"
      />
      <Breadcrumb items={[{ label: '客户案例' }]} />

      <main>
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            <Reveal>
              <SectionHeading
                title="项目索引"
                en="Client Cases"
                subtitle="展示华辉数智及九畴云市的项目列表与客户覆盖范围，所有案例数据均使用已确认信息，不虚构结果。"
              />
            </Reveal>

            {/* 筛选 */}
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-between">
              <div className="flex flex-wrap gap-2">
                {caseTypes.map((t) => (
                  <button
                    key={t}
                    onClick={() => setType(t)}
                    className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                      type === t
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {caseStatuses.map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                      status === s
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* 案例卡片 */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              {filtered.length === 0 ? (
                <div className="col-span-full text-center py-16 text-foreground-500">
                  <i className="ri-inbox-line text-3xl"></i>
                  <p className="mt-3 text-sm">未找到符合条件的案例。</p>
                </div>
              ) : (
                filtered.map((c) => (
                  <Reveal key={c.id}>
                    <div className="p-6 rounded-lg bg-background-100 border border-background-200 h-full flex flex-col">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-heading font-bold text-foreground-950 text-lg">{c.name}</h3>
                          <div className="mt-1 flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
                            <span className="whitespace-nowrap"><i className="ri-map-pin-line"></i> {c.region}</span>
                            <span className="whitespace-nowrap"><i className="ri-building-2-line"></i> {c.industry}</span>
                          </div>
                        </div>
                        <span className={`shrink-0 px-2.5 py-1 text-xs rounded-full whitespace-nowrap ${statusStyle[c.status]}`}>
                          {c.status}
                        </span>
                      </div>
                      <div className="mt-3 px-3 py-1.5 rounded-md bg-background-200/60 inline-flex text-xs text-foreground-700">
                        {c.type}
                      </div>
                      <div className="mt-4 space-y-2 text-sm text-foreground-600 leading-relaxed flex-1">
                        <p><span className="font-semibold text-foreground-900">项目场景：</span>{c.scene}</p>
                        <p><span className="font-semibold text-foreground-900">解决的问题：</span>{c.problem}</p>
                        <p><span className="font-semibold text-foreground-900">使用的解决方案：</span>{c.solution}</p>
                      </div>
                      <div className="mt-4 pt-4 border-t border-background-200 flex items-center justify-between">
                        <span className="text-xs text-foreground-500">{c.date}</span>
                        {c.href === '#' ? (
                          <span className="text-sm text-foreground-400 whitespace-nowrap">授权后公开</span>
                        ) : (
                          <Link
                            to={c.href}
                            className="text-sm text-primary-600 whitespace-nowrap cursor-pointer hover:text-primary-700"
                          >
                            查看详情 <i className="ri-arrow-right-line"></i>
                          </Link>
                        )}
                      </div>
                    </div>
                  </Reveal>
                ))
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}